import { useEffect, useMemo, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import {
  ENROLLMENT_STATUSES,
  type EnrollmentStatus,
  type TrialParticipantDto,
  type TrialParticipantsQuery,
  type TrialParticipantsSort,
} from '@ctms/contracts';
import { Search } from 'lucide-react';

import { ENROLLMENT_STATUS_LABELS as STATUS_FILTER_LABELS } from '../../patients/enrollment-status-labels.js';
import { createAppColumnHelper, useAppTable } from '../../../components/data-table/table-hook.js';
import { trialsApi } from '../trials.api.js';
import { trialQueryKeys } from '../trials.query-keys.js';
import { useTrialRandomization } from '../use-trial.js';

export interface TrialParticipantsProps {
  /** Active trial of the cockpit. `null` means "no trial picked yet" — then no request is sent. */
  trialId: number | null;
  canRead: boolean;
  onSelectPatient?: (patientId: number) => void;
}

const LIST_LIMIT = 25;

const columnHelper = createAppColumnHelper<TrialParticipantDto>();

/**
 * Polish labels of the nine participation statuses (element 11 of the Cockpit
 * legend), shared with the chart bar (element 6) through one canonical map.
 */
export { ENROLLMENT_STATUS_LABELS as STATUS_FILTER_LABELS } from '../../patients/enrollment-status-labels.js';

/** The dropdown order of the status filter: "Wszystkie" first, then the nine statuses. */
export const STATUS_FILTER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: 'Wszystkie' },
  ...Object.values(ENROLLMENT_STATUSES).map((status) => ({
    value: status,
    label: STATUS_FILTER_LABELS[status],
  })),
];

/**
 * The sort menu (tor C): "Domyślnie" first, then the four A-Z keys, 1:1 with
 * the elements the list shows. The list keeps a single "Numer" column that
 * JOINS both numbers, so the two numeric keys stay separate here.
 */
export const SORT_OPTIONS: readonly { value: '' | TrialParticipantsSort; label: string }[] = [
  { value: '', label: 'Domyślnie' },
  { value: 'lastName', label: 'Nazwisko A-Z' },
  { value: 'screeningNumber', label: 'Numer screeningowy A-Z' },
  { value: 'randomizationNumber', label: 'Numer randomizacyjny A-Z' },
  { value: 'status', label: 'Status A-Z' },
];

/**
 * The sort menu for one trial: the randomization key exists only when the trial
 * card keeps a randomization number (B.1/B.2). With the flag off the option is
 * absent from the menu, not greyed out.
 */
export function sortOptions(
  randomizationEnabled: boolean,
): readonly { value: '' | TrialParticipantsSort; label: string }[] {
  return randomizationEnabled
    ? SORT_OPTIONS
    : SORT_OPTIONS.filter((option) => option.value !== 'randomizationNumber');
}

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

function participantName(participant: TrialParticipantDto): string {
  return `${participant.patient.firstName} ${participant.patient.lastName}`;
}

function ParticipantNumber({
  participant,
  randomizationEnabled,
}: {
  participant: TrialParticipantDto;
  randomizationEnabled: boolean;
}): JSX.Element {
  const { screeningNumber, randomizationNumber } = participant.enrollment;
  return (
    <span className="flex items-baseline gap-1.5">
      {/* P-11: the screening number leads, the randomization one follows. */}
      <span className="font-medium text-slate-100">{screeningNumber}</span>
      {!randomizationEnabled || randomizationNumber === null ? null : (
        <span className="text-slate-400">{randomizationNumber}</span>
      )}
    </span>
  );
}

export function TrialParticipants({
  trialId,
  canRead,
  onSelectPatient,
}: TrialParticipantsProps): JSX.Element {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState<'' | TrialParticipantsSort>('');

  // B.1/B.2: the trial card decides whether a randomization number exists at all.
  const randomizationEnabled = useTrialRandomization(trialId);

  // When the flag turns off, the key leaves the menu — a stale selection must not
  // keep travelling to the API.
  useEffect(() => {
    if (!randomizationEnabled && sort === 'randomizationNumber') {
      setSort('');
    }
  }, [randomizationEnabled, sort]);

  const listFilters: TrialParticipantsQuery = useMemo(() => {
    const filters: TrialParticipantsQuery = { page, limit: LIST_LIMIT };
    const trimmed = search.trim();
    if (trimmed.length > 0) {
      filters.search = trimmed;
    }
    if (status.length > 0) {
      filters.status = status as EnrollmentStatus;
    }
    if (sort !== '') {
      filters.sort = sort;
    }
    return filters;
  }, [page, search, status, sort]);

  const participantsQuery = useQuery({
    queryKey: trialQueryKeys.enrollments(trialId ?? 0, listFilters),
    queryFn: () => trialsApi.listEnrollments(trialId ?? 0, listFilters),
    enabled: canRead && trialId !== null,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.display({
          id: 'number',
          header: 'Numer',
          cell: (info) => (
            <ParticipantNumber
              participant={info.row.original}
              randomizationEnabled={randomizationEnabled}
            />
          ),
        }),
        columnHelper.display({
          id: 'patient',
          header: 'Pacjent',
          cell: (info) => participantName(info.row.original),
        }),
        columnHelper.display({
          id: 'status',
          header: 'Status',
          cell: (info) => STATUS_FILTER_LABELS[info.row.original.enrollment.status],
        }),
        columnHelper.display({
          id: 'select',
          header: 'Wybór',
          cell: (info) => {
            const participant = info.row.original;
            return (
              <button
                type="button"
                aria-label={`Wybierz ${participantName(participant)}`}
                onClick={() => onSelectPatient?.(participant.patient.id)}
                className="rounded border border-cyan-800/60 px-2 py-1 text-xs text-cyan-300"
              >
                Wybierz
              </button>
            );
          },
        }),
      ]),
    [onSelectPatient, randomizationEnabled],
  );

  const items = participantsQuery.data?.items ?? [];
  const table = useAppTable({ data: items, columns });

  if (!canRead) {
    return (
      <section role="alert" className="rounded-lg border border-slate-800 bg-slate-900/60 p-8">
        <h2 className="text-base font-semibold text-slate-100">Brak dostępu do pacjentów badania</h2>
        <p className="mt-2 text-xs text-slate-400">
          Nie masz uprawnień do przeglądania listy pacjentów w badaniu. Jeżeli uważasz, że to błąd,
          skontaktuj się z administratorem ośrodka.
        </p>
      </section>
    );
  }

  if (trialId === null) {
    return (
      <section
        aria-label="Pacjenci w badaniu"
        className="rounded-lg border border-slate-800 bg-slate-900/60 p-8"
      >
        <h2 className="text-sm font-semibold text-slate-100">Pacjenci w badaniu</h2>
        <p className="mt-2 text-xs text-slate-400">
          Wybierz badanie, aby zobaczyć pacjentów.
        </p>
      </section>
    );
  }

  const retry = (): void => {
    void participantsQuery.refetch();
  };

  return (
    <section aria-label="Pacjenci w badaniu" className="space-y-3">
      <h2 className="text-sm font-semibold text-slate-100">Pacjenci w badaniu</h2>

      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute top-2 left-2.5 h-3.5 w-3.5 text-slate-500" />
          <input
            type="text"
            value={search}
            aria-label="Szukaj pacjenta"
            placeholder="Szukaj po nazwisku, imieniu lub numerze…"
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-slate-700 bg-slate-950 py-1.5 pr-3 pl-8 text-xs text-slate-100"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-400">
          Status
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
            className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 scheme-dark"
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <label htmlFor="participants-sort">Sortuj</label>
          <select
            id="participants-sort"
            value={sort}
            onChange={(event) => {
              setSort(event.target.value as '' | TrialParticipantsSort);
              setPage(1);
            }}
            className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 scheme-dark"
          >
            {sortOptions(randomizationEnabled).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {participantsQuery.isLoading ? (
        <p className="py-8 text-center text-xs text-slate-400">Ładowanie pacjentów badania</p>
      ) : participantsQuery.isError ? (
        <section role="alert" className="rounded-lg border border-rose-800/70 bg-rose-950/40 p-4">
          <p className="text-xs text-rose-200">Nie udało się wczytać listy pacjentów badania.</p>
          <button
            type="button"
            onClick={retry}
            className="mt-2 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200"
          >
            Spróbuj ponownie
          </button>
        </section>
      ) : items.length === 0 ? (
        <p className="rounded-lg border border-slate-800 bg-slate-900/60 p-6 text-center text-xs text-slate-400">
          Brak pacjentów w tym badaniu
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-left text-xs">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-slate-800 bg-slate-950/60">
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} scope="col" className="px-3 py-2 text-slate-400">
                      <table.FlexRender header={header} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b border-slate-800/80">
                  {row.getAllCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2">
                      <table.FlexRender cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!participantsQuery.isLoading &&
      !participantsQuery.isError &&
      participantsQuery.data &&
      (participantsQuery.data.page > 1 || participantsQuery.data.hasNext) ? (
        <div className="flex gap-2">
          <button
            type="button"
            disabled={participantsQuery.data.page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 disabled:opacity-40"
          >
            Poprzednia strona
          </button>
          <button
            type="button"
            disabled={!participantsQuery.data.hasNext}
            onClick={() => setPage((current) => current + 1)}
            className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 disabled:opacity-40"
          >
            Następna strona
          </button>
        </div>
      ) : null}
    </section>
  );
}